type WakeLockType = 'screen'

interface WakeLockSentinelLike {
  release: () => Promise<void>
  addEventListener: (type: 'release', listener: () => void) => void
}

interface WakeLockApi {
  request: (type: WakeLockType) => Promise<WakeLockSentinelLike>
}

function getApi(): WakeLockApi | null {
  if (typeof navigator === 'undefined') return null
  const candidate = (navigator as unknown as { wakeLock?: WakeLockApi }).wakeLock
  return candidate ?? null
}

// The screen Wake Lock is a single OS-level resource, so we hold at most one
// platform sentinel and keep it alive while any owner still wants the screen
// awake. Owners (the workout timer, a video upload, …) register/deregister by
// token; the platform lock is released only once the last owner lets go, so one
// feature finishing can't drop a lock another feature is still relying on.
const owners = new Set<symbol>()
let sentinel: WakeLockSentinelLike | null = null
let acquireInFlight: Promise<void> | null = null

async function sync(): Promise<void> {
  if (owners.size === 0) {
    await dropSentinel()
  } else {
    await ensureSentinel()
  }
}

async function ensureSentinel(): Promise<void> {
  if (sentinel) return
  if (acquireInFlight) return acquireInFlight
  const api = getApi()
  if (!api) return
  // Browsers refuse the request when the document is not visible.
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return

  acquireInFlight = (async () => {
    try {
      const next = await api.request('screen')
      next.addEventListener('release', () => {
        if (sentinel === next) sentinel = null
      })
      // Everyone let go (or the tab hid) while we were awaiting — don't keep it.
      if (owners.size === 0) {
        await next.release().catch(() => {})
        return
      }
      sentinel = next
    } catch {
      // Permission denied, low battery, etc. — silent.
    } finally {
      acquireInFlight = null
    }
  })()
  return acquireInFlight
}

async function dropSentinel(): Promise<void> {
  if (!sentinel) return
  const current = sentinel
  sentinel = null
  try {
    await current.release()
  } catch {
    // ignore
  }
}

/**
 * Register `owner`'s desire to keep the screen awake (idempotent per owner).
 * Callers should re-invoke this when the tab becomes visible again, since
 * browsers release the platform lock automatically while the page is hidden.
 */
export async function acquireWakeLock(owner: symbol): Promise<void> {
  owners.add(owner)
  await sync()
}

/** Drop `owner`'s hold; releases the platform lock once no owner remains. */
export async function releaseWakeLock(owner: symbol): Promise<void> {
  owners.delete(owner)
  await sync()
}

export function hasWakeLock(): boolean {
  return sentinel !== null
}
